import { useCallback, useMemo } from "react";
import type { AgentTemplate, ProjectAgent } from "@/api/agents";
import type {
  InputBarFocusedAgent,
  InputBarProvider,
} from "@/components/panels/input-bar-model-pill";
import {
  useCompanionStore,
  type AvailableModelsMap,
} from "@/stores/companion-store";
import {
  useInputBarTarget,
  type InputBarTarget,
} from "@/stores/input-bar-target-store";
import {
  buildDenkerComposerAgentInfo,
  providerRuntimeId,
  runtimeIdForSource,
} from "@/lib/denker-composer-agent";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import { getModelEffortConfig, isRuntimeKey } from "@/lib/model-registry";

export function useDenkerComposerAgent(
  projectAgents: ProjectAgent[],
  agentTemplates: AgentTemplate[],
  currentSpaceId: string | null | undefined,
) {
  const sessionTokens = useCompanionStore((s) => s.sessionTokens);
  const agentSessions = useCompanionStore((s) => s.agentSessions);
  const availableModels = useCompanionStore((s) => s.availableModels);
  const focusedAgent = useMemo(
    () =>
      buildDenkerComposerAgentInfo({
        projectAgents,
        agentTemplates,
        currentSpaceId,
        sessionTokens,
        agentSessions,
        availableModels,
      }),
    [
      projectAgents,
      agentTemplates,
      currentSpaceId,
      sessionTokens,
      agentSessions,
      availableModels,
    ],
  );
  // Frame-local selections must not retarget the user-facing model picker.
  const getFocusedAgentInfo: (agentId: string | null) => typeof focusedAgent =
    useCallback(() => focusedAgent, [focusedAgent]);
  return { focusedAgent, getFocusedAgentInfo };
}

/**
 * The model a reply in the input bar's selected chat runs on: the chat's own
 * runtime while it is available and the backend has not expired it, otherwise
 * the template agent.
 */
export function inputBarChatAgent(
  templateAgent: InputBarFocusedAgent | null,
  target: InputBarTarget | null,
  providers: InputBarProvider[],
  availableModels: AvailableModelsMap = {},
): InputBarFocusedAgent | null {
  // A sub-agent reply runs on its own native runtime; no chat model applies.
  if (isSubagentTarget(target)) return null;
  const adapterType = target?.runtimeAdapterType;
  const model = target?.runtimeModel;
  if (
    !templateAgent ||
    !target ||
    target.runtimeOverrideExpired ||
    target.projectAgentId !== templateAgent.id ||
    !model ||
    !adapterType ||
    !isRuntimeKey(adapterType)
  ) {
    return templateAgent;
  }
  // Managed and personal OpenCode are separate runtimes: match the exact one.
  // Mirrors the backend: a logged-out runtime or a model missing from a
  // non-empty catalog makes the reply fall back to the template.
  const runtimeId = runtimeIdForSource(adapterType, target.runtimeSource);
  const provider = providers.find(
    (candidate) => providerRuntimeId(candidate) === runtimeId,
  );
  const catalog = availableModels[runtimeId];
  if (
    !provider ||
    provider.detected === false ||
    provider.loggedIn === false ||
    (catalog?.length &&
      !catalog.some(
        (entry) => entry.id === model || (entry.aliases ?? []).includes(model),
      ))
  ) {
    return templateAgent;
  }
  const effort =
    getModelEffortConfig(model).values.find(
      (value) => value === target.runtimeThinkingEffort,
    ) ?? "default";
  return {
    ...templateAgent,
    adapterType,
    runtimeId,
    model,
    effort,
  };
}

/** Pill-only chat model; conversation frames keep the template baseline. */
export function useInputBarChatAgent(
  templateAgent: InputBarFocusedAgent | null,
  providers: InputBarProvider[],
  enabled: boolean,
): InputBarFocusedAgent | null {
  const target = useInputBarTarget((state) => state.target);
  const availableModels = useCompanionStore((state) => state.availableModels);
  return useMemo(
    () =>
      enabled
        ? inputBarChatAgent(templateAgent, target, providers, availableModels)
        : templateAgent,
    [enabled, templateAgent, target, providers, availableModels],
  );
}
